import { useState } from 'react'
import { Button } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/data-state'
import { ConfirmDialog } from '@/components/ui/form-modal'
import { PlusIcon } from '@/components/ui/icons'
import { translated } from '@/lib/api/common'

import { useChoiceGroupActions, useChoiceGroupList } from '../hooks'
import type { ChoiceGroup } from '../types'
import { ChoiceGroupFormDialog } from './choice-group-form-dialog'
import { ChoiceGroupsTable } from './choice-groups-table'
import type { ChoiceGroupRowAction } from './choice-groups-table'

/**
 * Add-ons a customer picks on top of a product.
 *
 * One tab of the Products module. Choice groups, product options and the
 * catalogue itself are the same job — setting up a menu — and a merchant doing
 * it moves between the three constantly; separate modules made that a trip
 * through home each time.
 */
export function ChoiceGroupsPanel() {
  const { groups, isLoading, isRefreshing, error, retry } = useChoiceGroupList()
  const { remove, reorder } = useChoiceGroupActions()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ChoiceGroup | null>(null)

  function openForm(id: string | null) {
    setEditingId(id)
    setIsFormOpen(true)
  }

  function move(group: ChoiceGroup, delta: number) {
    const ids = groups.map((item) => item.id)
    const from = ids.indexOf(group.id)
    const to = from + delta

    if (from < 0 || to < 0 || to >= ids.length) return

    ids.splice(to, 0, ...ids.splice(from, 1))
    reorder.mutate(ids)
  }

  function handleRowAction(action: ChoiceGroupRowAction, group: ChoiceGroup) {
    switch (action) {
      case 'edit':
        return openForm(group.id)
      case 'moveUp':
        return move(group, -1)
      case 'moveDown':
        return move(group, 1)
      case 'delete':
        setPendingDelete(group)
        return
    }
  }

  return (
    <>
      <ModuleIntro
        description="Add-ons a customer picks while ordering — sizes, sauces, extras. The order here is the order they see."
        action={
          <Button
            variant="primary"
            size="sm"
            className="h-9 rounded-full px-4 font-medium"
            onPress={() => openForm(null)}
          >
            <PlusIcon width={16} height={16} />
            New group
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your choice groups" error={error} onRetry={retry} />
      ) : groups.length === 0 ? (
        <EmptyState
          title="No choice groups yet."
          description="Sizes, sauces, extras — anything a customer picks on top of a product."
          action={{ label: 'New group', onPress: () => openForm(null) }}
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <ChoiceGroupsTable
              groups={groups}
              onAction={handleRowAction}
              busyIds={remove.isPending && remove.variables ? [remove.variables] : []}
              isReordering={reorder.isPending}
            />
          </div>

          <p className="text-muted text-xs tabular-nums">
            {groups.length} {groups.length === 1 ? 'group' : 'groups'}
          </p>
        </>
      )}

      <ChoiceGroupFormDialog
        key={editingId ?? 'new'}
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        groupId={editingId}
        onSaved={() => setIsFormOpen(false)}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        onOpenChange={(isOpen) => !isOpen && setPendingDelete(null)}
        title="Delete this choice group?"
        confirmLabel="Delete"
        isBusy={remove.isPending}
        error={remove.error}
        errorFallback="Could not delete this group. One still attached to a product cannot be removed."
        onConfirm={async () => {
          if (!pendingDelete) return
          await remove.mutateAsync(pendingDelete.id)
          setPendingDelete(null)
        }}
        body={
          <>
            <span className="text-foreground font-medium">
              {pendingDelete ? translated(pendingDelete.name) : ''}
            </span>{' '}
            and all of its options will be removed. Products using it lose the add-on.
          </>
        }
      />
    </>
  )
}
